// 마을 퀘스트 추적기의 개인 설정(표시·접힘 모드)과 배치 계산. DOM을 만지지 않는 순수 모듈이라 단위 시험이 쉽다.
// 저장: localStorage 'dungeonhero.ui.questCard'(기기 전용, account/keys.ts LOCAL_ONLY_KEYS). 조이스틱 입력 영역 계산도 여기 둬서 추적기와 영역이 겹치지 않음을 시험으로 지킨다.
// 대표님 2026-10-24: "왼쪽 퀘스트 창은 항상 저렇게 있으니까 매우 불편하다" → 상시 큰 카드를 작은 추적기로 줄이고 자세한 내용은 별도 퀘스트 창(questWindow.ts)으로 옮겼다. 근거·측정은 docs/UI-QUEST-PC.md.

import { bountiesFor, type AdventureView } from '../shared/adventure';

export interface Rect { x: number; y: number; w: number; h: number }

/** 추적기 모드: min = 아이콘 하나, line = 한 줄 추적기(목표 이름·수치·「자동」) */
export type TrackerMode = 'min' | 'line';
export const TRACKER_MODES: readonly TrackerMode[] = ['min', 'line'];

export interface QuestCardPrefs {
  /** 추적기 표시(설정). 끄면 마을에서 아예 그리지 않는다(퀘스트 창은 단축키·가방 줄 버튼으로 연다) */
  visible: boolean;
  /** null이면 화면과 진행으로 정한다(폰은 1-1 클리어 뒤 아이콘 하나, 처음 온 이용자·넓은 화면은 한 줄) */
  tracker: TrackerMode | null;
}

export const QUEST_CARD_KEY = 'dungeonhero.ui.questCard';
/** 설정이 바뀌면 window에 알린다(detail = QuestCardPrefs) */
export const QUEST_CARD_EVENT = 'dh-quest-card-prefs';

/** 추적기 위치·폭의 설계 값(1280x720 기준). 폰에서는 글자가 읽히도록 배율 하한을 둔다 */
export const CARD_TOP = 130, CARD_LEFT = 12, LINE_WIDTH = 340, MIN_SCALE = 0.7;
/** 추적기 폭이 화면 폭의 이 비율을 넘지 않게 한다(오른쪽에 조이스틱 자리를 남기려고). 세로 화면은 PORTRAIT_MAX_WIDTH_FRAC */
export const MAX_WIDTH_FRAC = 0.34;
/** 터치 가로 화면의 폭 하한(설계 px, 글자 칸 약 38px + 아이콘·자동·접기 단추 실측 약 235)과 화면 폭 상한 비율 */
export const TOUCH_LINE_MIN_WIDTH = 290, TOUCH_MAX_WIDTH_FRAC = 0.45;
export const PORTRAIT_MAX_WIDTH_FRAC = 0.84, PORTRAIT_MIN_SCALE = 0.9;
/** 터치로 누를 수 있는 최소 크기(CSS px, 화면 기준) */
export const MIN_TOUCH = 44;
/** 글자는 화면 기준 12px 이상 */
export const MIN_BODY_PX = 12;
/** 짧은 변이 이보다 작으면 폰으로 보고 기본을 아이콘 하나로 한다(대표님 지시: 휴대폰 짧은 변 < 500px) */
export const PHONE_SHORT_SIDE = 500;

export const defaultQuestCardPrefs = (): QuestCardPrefs => ({ visible: true, tracker: null });

export function parseQuestCardPrefs(raw: string | null): QuestCardPrefs {
  const p = defaultQuestCardPrefs();
  if (!raw) return p;
  try {
    const j = JSON.parse(raw) as Partial<Record<string, unknown>>;
    if (typeof j.visible === 'boolean') p.visible = j.visible;
    if (typeof j.tracker === 'string' && (TRACKER_MODES as readonly string[]).includes(j.tracker)) p.tracker = j.tracker as TrackerMode;
    // 옛 저장(2026-10-20 카드): 접어 둔 사람은 아이콘 하나로 옮긴다. 크기(size)는 더 쓰지 않는다
    else if (j.collapsed === true) p.tracker = 'min';
  } catch { /* 깨진 값: 기본값 */ }
  return p;
}

/** 이번 접속에서 바꾼 값. 저장소를 못 써도(사생활 보호 창 등) 이 값으로 접속 동안은 상태가 유지된다 */
let memory: QuestCardPrefs | null = null;

export function loadQuestCardPrefs(): QuestCardPrefs {
  if (memory) return { ...memory };
  try {
    return parseQuestCardPrefs(typeof localStorage !== 'undefined' ? localStorage.getItem(QUEST_CARD_KEY) : null);
  } catch { return defaultQuestCardPrefs(); }
}

/** 일부만 바꿔 저장하고 알린다 */
export function updateQuestCardPrefs(patch: Partial<QuestCardPrefs>): QuestCardPrefs {
  const p = { ...loadQuestCardPrefs(), ...patch };
  memory = p;
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(QUEST_CARD_KEY, JSON.stringify({ visible: p.visible, tracker: p.tracker })); } catch { /* 저장 실패는 무시 */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(QUEST_CARD_EVENT, { detail: p }));
  return p;
}
/** 시험용: 메모리 사본을 비운다 */
export function resetQuestCardMemory(): void { memory = null; }

/** 저장된 모드가 없으면 화면·진행으로 정한다: 짧은 변이 PHONE_SHORT_SIDE 미만(폰)이고 1-1을 한 번이라도 깼으면(또는 진행을 모르면) 아이콘 하나, 폰이라도 처음 온 이용자(cleared 0)나 넓은 화면은 한 줄 */
export function resolveTrackerMode(mode: TrackerMode | null, w: number, h: number, cleared = -1): TrackerMode {
  // 신규 이용자(첫 스테이지 1-1을 아직 못 깸, cleared === 0)는 폰에서도 한 줄(목표 글자 + 자동 이동)로 보여 던전 가는 길을 찾게 한다(페르소나 QA P1).
  // 저장된 사용자 선택(mode)은 항상 우선한다. cleared가 -1(모름)이면 예전 기본(폰 = 아이콘).
  return mode ?? (Math.min(w, h) < PHONE_SHORT_SIDE && cleared !== 0 ? 'min' : 'line');
}

/** 아이콘 ↔ 한 줄 전환. 저장하고 알린다(저장된 값은 이후 화면 크기와 상관없이 우선한다) */
export function toggleTracker(w: number, h: number, cleared = -1): QuestCardPrefs {
  const cur = resolveTrackerMode(loadQuestCardPrefs().tracker, w, h, cleared);
  return updateQuestCardPrefs({ tracker: cur === 'min' ? 'line' : 'min' });
}

export interface TrackerLayout {
  /** 한 줄 추적기 배율(설계 px → 화면 px) */
  scale: number;
  /** 화면 기준 폭(px) */
  width: number;
}
export const newTrackerLayout = (): TrackerLayout => ({ scale: 1, width: LINE_WIDTH });

/**
 * 한 줄 추적기 배율·폭. 디자인 배율(하한 MIN_SCALE) 그대로, 폭은 화면의 상한 비율(가로 0.34W·세로 0.84W)을 넘지 않는다.
 * 세로 화면은 배율 0.9 아래로 내리지 않는다. screenH를 안 주면 가로로 본다. out을 주면 그 객체에 써서 돌려준다(매 프레임 경로에서 할당 없음).
 */
export function trackerLayout(u: number, screenW: number, screenH = 0, out: TrackerLayout = newTrackerLayout(), touch = false): TrackerLayout {
  const portrait = screenH > screenW;
  const floor = portrait ? PORTRAIT_MIN_SCALE : MIN_SCALE;
  const frac = portrait ? PORTRAIT_MAX_WIDTH_FRAC : MAX_WIDTH_FRAC;
  const scale = Math.max(u, floor);
  out.scale = scale;
  let width = Math.min(LINE_WIDTH * scale, frac * screenW);
  // 터치 가로 화면(폰 568x320)에서 0.34W는 아이콘·자동·접기(44px) 단추에 밀려 글자 칸이 28px 아래로 줄었다(#239 스모크).
  // 글자 칸이 28px 이상(실측 약 38px) 남도록 폭 하한(설계 TOUCH_LINE_MIN_WIDTH × 배율)을 두되, 화면의 TOUCH_MAX_WIDTH_FRAC는 넘기지 않는다(조이스틱 자리).
  if (touch && !portrait) width = Math.max(width, Math.min(TOUCH_LINE_MIN_WIDTH * scale, TOUCH_MAX_WIDTH_FRAC * screenW));
  out.width = Math.round(width);
  return out;
}

/** 아이콘 모드 단추의 한 변(화면 px). 터치가 아니어도 누르기 쉬운 크기를 지킨다 */
export const MIN_BUTTON = MIN_TOUCH;

/**
 * 추적기 위쪽(화면 px). 세로 화면은 추적기가 넓어져(0.84W) 오른쪽 위 미니맵(아래 끝 약 125px)에 닿으므로 그 아래로 내린다(390x844 실측).
 * 세로는 높이가 넉넉해 내려도 조이스틱 자리가 남는다.
 */
export const PORTRAIT_CARD_TOP_MIN = 136;
export function cardTopPx(u: number, screenW = 0, screenH = 0): number {
  const top = Math.round(CARD_TOP * u);
  return screenH > screenW ? Math.max(top, PORTRAIT_CARD_TOP_MIN) : top;
}

/**
 * 추적기가 화면에 놓인 사각형의 추정(시험·측정 전후 비교용). 실제 DOM 높이는 villageQuest.ts가 재서 villageHudLayout.rect로 알리고,
 * 이 추정은 QA(실브라우저 측정)와 맞춰 둔 같은 식이다: 한 줄 = 아이콘 38k + 위아래 여백 12k + 테두리 3, 터치는 44px 칸을 넘지 않게.
 */
export function trackerRect(u: number, screenW: number, screenH: number, mode: TrackerMode, touch: boolean, chip = false): Rect {
  const top = cardTopPx(u, screenW, screenH);
  if (mode === 'min') return { x: CARD_LEFT, y: top, w: MIN_BUTTON, h: MIN_BUTTON };
  const lay = trackerLayout(u, screenW, screenH, undefined, touch);
  const k = lay.scale;
  const inner = Math.max(Math.round(38 * k), touch ? MIN_BUTTON : 0);
  return { x: CARD_LEFT, y: top, w: lay.width, h: inner + Math.round(12 * k) + 3 + (chip ? chipExtraH(k, touch) : 0) };
}

/** 현상금 칩 높이(화면 px): 설계 24 × 배율, 터치는 44 이상(출시 게이트 RG-008: 겉 크기 44 미만 금지) */
export const CHIP_TOUCH_H = MIN_TOUCH;
export function chipHeight(k: number, touch: boolean): number { return Math.max(Math.round(24 * k), touch ? CHIP_TOUCH_H : 0); }
/** 한 줄 추적기 아래에 칩 줄이 더해 주는 높이 = 줄 간격(8k) + 칩 높이 */
export function chipExtraH(k: number, touch: boolean): number { return Math.round(8 * k) + chipHeight(k, touch); }

/** 오늘 현상금 상태: 전체 수·남은 수. adventure 정보가 없으면 null(칩을 그리지 않는다) */
export interface BountyStatus { remaining: number; total: number }
export function bountyStatus(adv: Pick<AdventureView, 'day' | 'done' | 'highestTown'> | null | undefined): BountyStatus | null {
  if (!adv) return null;
  const list = bountiesFor(adv.day, adv.highestTown);
  let remaining = 0;
  for (const b of list) if (!adv.done.includes(b.id)) remaining++;
  return { remaining, total: list.length };
}
/** 칩 글자: 남았으면 「⚔ 오늘 현상금 N개 남음」, 다 했으면 「현상금 완료 ✓」 */
export function bountyChipText(s: BountyStatus): string {
  return s.total > 0 && s.remaining === 0 ? '현상금 완료 ✓' : '⚔ 오늘 현상금 ' + s.remaining + '개 남음';
}

/** 두 사각형이 gap 이상 떨어져 있지 않으면 겹친 것으로 본다 */
export function rectsOverlap(a: Rect, b: Rect, gap = 0): boolean {
  return a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;
}

/** 사각형들의 합집합 면적(겹침은 한 번만). y 경계마다 가로 구간을 합친다 */
export function unionArea(rects: readonly Rect[]): number {
  if (!rects.length) return 0;
  const ys = Array.from(new Set(rects.flatMap((p) => [p.y, p.y + p.h]))).sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i + 1 < ys.length; i++) {
    const y0 = ys[i], y1 = ys[i + 1];
    const spans = rects.filter((p) => p.y <= y0 && p.y + p.h >= y1).map((p) => [p.x, p.x + p.w] as const).sort((a, b) => a[0] - b[0]);
    let cur = -Infinity, len = 0;
    for (const [a, b] of spans) { if (b <= cur) continue; len += b - Math.max(a, cur); cur = b; }
    area += len * (y1 - y0);
  }
  return area;
}

/** 사각형들을 area 안으로 잘라 낸 조각(밖은 버린다) */
function clipTo(rects: readonly Rect[], area: Rect): Rect[] {
  const out: Rect[] = [];
  for (const r of rects) {
    const x0 = Math.max(r.x, area.x), x1 = Math.min(r.x + r.w, area.x + area.w), y0 = Math.max(r.y, area.y), y1 = Math.min(r.y + r.h, area.y + area.h);
    if (x1 > x0 && y1 > y0) out.push({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
  }
  return out;
}

/**
 * 플레이 영역 중앙 50%(가로 50% × 세로 50%, 화면 한가운데)를 UI 사각형들이 가리는 비율(0..1). 겹친 부분은 한 번만 센다.
 * 대표님 규칙 "UI는 플레이를 가리지 않는다"의 측정 기준(docs/UI-QUEST-PC.md).
 */
export function centerOcclusion(rects: readonly Rect[], screenW: number, screenH: number): number {
  const c: Rect = { x: screenW * 0.25, y: screenH * 0.25, w: screenW * 0.5, h: screenH * 0.5 };
  return unionArea(clipTo(rects, c)) / (c.w * c.h);
}

/** 화면 전체에서 사각형들이 차지하는 비율(0..1, 겹침은 한 번만) */
export function screenCoverage(rects: readonly Rect[], screenW: number, screenH: number): number {
  return unionArea(clipTo(rects, { x: 0, y: 0, w: screenW, h: screenH })) / (screenW * screenH);
}

/** 조이스틱 입력 영역: 화면 왼쪽 48% × 아래 72%. 카드가 이 영역을 덮으면 카드를 피해 나눈다 */
export const JOY_W_FRAC = 0.48, JOY_H_FRAC = 0.72;
/** 영역 조각의 최소 변(px). 이보다 좁으면 엄지로 잡기 어려워 버린다 */
export const JOY_MIN = 96;
/** 화면 맨 아래 띠 조각의 최소 높이(px). 폰 가로(844x390)에서 카드·골드 줄 아래 남는 띠가 약 85px라
 *  96px 기준이면 버려져 엄지가 자연스럽게 놓이는 왼쪽 아래 모서리가 조이스틱이 아니었다. 엄지 접촉(약 45~57px)보다 넉넉한 64px로 둔다 */
export const JOY_STRIP_MIN_H = 64;
const JOY_GAP = 6;

/** 기본 조이스틱 영역(카드가 없을 때) */
export function baseJoystickZone(screenW: number, screenH: number): Rect {
  return { x: 0, y: screenH * (1 - JOY_H_FRAC), w: screenW * JOY_W_FRAC, h: screenH * JOY_H_FRAC };
}

/**
 * 조이스틱 입력 영역 조각들. 카드가 기본 영역을 덮으면 카드를 피해 ㄴ자로 나눈다:
 *  - 오른쪽 조각: 카드 오른쪽 가장자리 + 틈부터 영역 오른쪽 끝까지, 영역 전체 높이
 *  - 아래 조각: 영역 왼쪽부터 카드 오른쪽 끝까지, 카드 아래 + 틈부터 화면 아래까지
 * 조각끼리는 겹치지 않고, 어느 조각도 카드와 겹치지 않는다. 변이 JOY_MIN(아래 띠의 높이는 JOY_STRIP_MIN_H)보다 짧은 조각은 버린다.
 */
export function joystickZones(screenW: number, screenH: number, card: Rect | null): Rect[] {
  const base = baseJoystickZone(screenW, screenH);
  if (!card || !rectsOverlap(base, card)) return [base];
  const out = splitZones(base, card, JOY_MIN, JOY_STRIP_MIN_H);
  if (out.length) return out;
  // 아주 작은 가로 화면(640x360 등): 기준을 넘는 조각이 없으면 조이스틱이 통째로 사라져 마을에서 못 움직인다.
  // 엄지 접촉보다 넉넉한 최소값으로 다시 나누고, 그래도 없으면 카드를 무시한 기본 영역을 쓴다(이동이 막히는 것보다 낫다)
  const loose = splitZones(base, card, JOY_FALLBACK_MIN, JOY_FALLBACK_STRIP_H);
  return loose.length ? loose : [base];
}

/** 아주 작은 화면용 조각 최소 폭·띠 높이(px) */
export const JOY_FALLBACK_MIN = 64, JOY_FALLBACK_STRIP_H = 40;

function splitZones(base: Rect, card: Rect, minW: number, stripH: number): Rect[] {
  const out: Rect[] = [];
  const baseR = base.x + base.w, baseB = base.y + base.h;
  const rx = card.x + card.w + JOY_GAP;
  if (baseR - rx >= minW) out.push({ x: rx, y: base.y, w: baseR - rx, h: base.h });
  const by = card.y + card.h + JOY_GAP;
  const bw = Math.min(baseR, rx - JOY_GAP) - base.x;
  if (baseB - by >= stripH && bw >= minW) out.push({ x: base.x, y: by, w: bw, h: baseB - by });
  return out;
}

/** 점이 조각 중 하나 안에 있는가 */
export function inZones(zones: readonly Rect[], x: number, y: number): boolean {
  for (const z of zones) if (x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h) return true;
  return false;
}

/**
 * 마을 HUD 세로 배치 공유값. bottom < 0이면 카드가 없다(골드·가방 줄은 기존 자리).
 * rect = 카드가 화면에 차지한 사각형, row = 그 아래 골드·가방 줄(rpg.ts)의 사각형. 없으면 null.
 * 둘 다 누를 수 있는 HUD라 조이스틱 영역이 피해야 한다(touch.ts → joystickZones).
 */
export const villageHudLayout: { bottom: number; version: number; rect: Rect | null; row: Rect | null } = { bottom: -1, version: 0, rect: null, row: null };

/** 카드와 그 아래 골드·가방 줄을 한 덩어리로 묶은 바깥 사각형(둘 중 없는 것은 뺀다) */
export function hudBlockRect(card: Rect | null, row: Rect | null): Rect | null {
  if (!card) return row;
  if (!row) return card;
  const x = Math.min(card.x, row.x), y = Math.min(card.y, row.y);
  return { x, y, w: Math.max(card.x + card.w, row.x + row.w) - x, h: Math.max(card.y + card.h, row.y + row.h) - y };
}
